import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { UserAddress } from '../types';

const canadianProvinces = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

interface AddressFormProps {
  address?: UserAddress;
  onSubmit: (address: Omit<UserAddress, 'id' | 'user_id'>) => void;
  onCancel: () => void;
}

const AddressForm: React.FC<AddressFormProps> = ({ address, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: 'BC',
    postal_code: '',
    country: 'Canada',
    phone_number: '',
  });

  const [postalCodeError, setPostalCodeError] = useState('');

  useEffect(() => {
    if (address) {
      setFormData({
        address_line_1: address.address_line_1,
        address_line_2: address.address_line_2 || '',
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: 'Canada', // Always Canada
        phone_number: address.phone_number || '',
      });
    }
  }, [address]);

  const validatePostalCode = (postalCode: string): boolean => {
    // Canadian postal code pattern: A1A 1A1 (letter-digit-letter space digit-letter-digit)
    const canadianPostalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
    return canadianPostalCodeRegex.test(postalCode);
  };

  const formatPostalCode = (value: string): string => {
    // Remove any existing spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    
    // Add space after 3 characters if length is greater than 3
    if (cleaned.length > 3) {
      return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)}`;
    }
    return cleaned;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'postal_code') {
      const formattedValue = formatPostalCode(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      
      // Validate postal code format
      if (formattedValue && !validatePostalCode(formattedValue)) {
        setPostalCodeError('Please enter a valid Canadian postal code (e.g., V6B 1A1)');
      } else {
        setPostalCodeError('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Final validation
    if (!validatePostalCode(formData.postal_code)) {
      setPostalCodeError('Please enter a valid Canadian postal code (e.g., V6B 1A1)');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Street Address"
        name="address_line_1"
        value={formData.address_line_1}
        onChange={handleChange}
        required
        fullWidth
      />
      
      <TextField
        label="Address Line 2 (Optional)"
        name="address_line_2"
        value={formData.address_line_2}
        onChange={handleChange}
        fullWidth
      />
      
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <TextField
          label="City"
          name="city"
          value={formData.city}
          onChange={handleChange}
          required
          fullWidth
        />
        <FormControl fullWidth required>
          <InputLabel>Province</InputLabel>
          <Select
            name="state"
            value={formData.state}
            onChange={handleSelectChange}
            label="Province"
          >
            {canadianProvinces.map((province) => (
              <MenuItem key={province.code} value={province.code}>
                {province.name} ({province.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <TextField
          label="Postal Code"
          name="postal_code"
          value={formData.postal_code}
          onChange={handleChange}
          required
          fullWidth
          placeholder="V6B 1A1"
          error={!!postalCodeError}
          helperText={postalCodeError || 'Format: A1A 1A1'}
        />
        <TextField
          label="Country"
          name="country"
          value={formData.country}
          disabled
          fullWidth
          sx={{ 
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
            }
          }}
        />
      </Box>
      
      <TextField
        label="Phone Number (Optional)"
        name="phone_number"
        type="tel"
        value={formData.phone_number}
        onChange={handleChange}
        fullWidth
        placeholder="(604) 123-4567"
      />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="outlined">
          {address ? 'Update Address' : 'Save Address'}
        </Button>
      </Box>
    </Box>
  );
};

export default AddressForm; 